import { Component, inject, signal } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatIconRegistry } from '@angular/material/icon';
import { MatSidenavModule } from '@angular/material/sidenav';
import { DomSanitizer } from '@angular/platform-browser';
import { RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from "moment";
import 'moment/locale/fr';
import { environment } from '../../../environments/environment';
import { MpsDateFormatService } from '../../services/mps-dateformat.service';
import { MpsUserService } from '../../services/mps-user.service';
import { MpsFooterComponent } from '../mps-footer/mps-footer.component';
import { MpsHeaderComponent } from '../mps-header/mps-header.component';
import { MpsLoaderComponent } from '../mps-loader/mps-loader.component';
import { MpsNavigationComponent } from '../mps-navigation/mps-navigation.component';

@Component({
  selector: 'mps-app-root',
  templateUrl: './mps-app.component.html',
  styleUrl: './mps-app.component.scss',
  imports: [
    MpsLoaderComponent,
    MpsHeaderComponent,
    MpsFooterComponent,
    MpsNavigationComponent,
    TranslateModule,
    MatSidenavModule,
    RouterOutlet,
  ],
})
export class MpsAppComponent {
  private readonly _matIconRegistry = inject(MatIconRegistry);
  private readonly _domSanitizer = inject(DomSanitizer);
  private readonly _translateService = inject(TranslateService);
  private readonly _userService = inject(MpsUserService);
  private readonly _user = signal(this._userService.getUserSubject().getValue());
  private readonly dateFormatService = inject(MpsDateFormatService);

  public constructor(private readonly dateAdapter: DateAdapter<any>) {
    this.dateAdapter.setLocale(environment.defaultLocale);
    this.registerSvgIcon();
    this.initTranslationService();
  }

  private initTranslationService() {
    this._translateService.setDefaultLang(environment.defaultLocale);
    this._translateService.addLangs(environment.allowedLanguage);

    // Set a default language if there's no language set
    let langToUse = this._translateService.currentLang;
    if (!langToUse) {
      const userLang = this._user()?.lang;
      langToUse = userLang && this._translateService.getLangs().includes(userLang)
        ? userLang
        : (localStorage.getItem('lang') as string) || environment.defaultLocale;
      this._translateService.use(langToUse);
    }

    moment.locale(langToUse);
    this.dateFormatService.setLocale(langToUse);

    this._translateService.onLangChange.subscribe((event) => {
      moment.locale(event.lang);
      this.dateFormatService.setLocale(event.lang);
    });
  }

  private registerSvgIcon() {
    const icons = [...environment.mpsIcons, ...environment.appIcons];

    icons.forEach((icon) => {
      this._matIconRegistry.addSvgIcon(
        `total-${icon}`,
        this._domSanitizer.bypassSecurityTrustResourceUrl(`../../../assets/icons/${icon}.svg`)
      );
    });
  }
}
